"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.invalidateLetterSignatures = invalidateLetterSignatures;
exports.resetLetterSignaturesStore = resetLetterSignaturesStore;
exports.useLetterSignatures = useLetterSignatures;
/**
 * Podpisy pism na liście: jedno zbiorcze zapytanie zamiast jednego na wiersz.
 *
 * Każdy wiersz listy pism prosi o swoje podpisy przez `useLetterSignatures(letterId)`. Prośby z jednego
 * „tchnienia” renderowania (kilkadziesiąt wierszy) zbiera krótki timer i wysyła jako jedno
 * POST /letters/signatureSummary. Wynik jest podręczny, dopóki ktoś nie wywoła
 * `invalidateLetterSignatures` (po udanym podpisie albo wgraniu pliku).
 *
 * Plakietka jest ozdobą informacyjną: błąd zapytania nie może zepsuć listy ani sypać komunikatami,
 * więc wiersz po prostu zostaje bez plakietki.
 */
const react_1 = require("react");
const SigningApi_1 = require("../../Signing/SigningApi");
/** Okno zbierania próśb z jednego renderu listy. */
const BATCH_DELAY_MS = 40;
/** Serwer przyjmuje najwyżej tyle pism naraz (MAX_LETTERS_PER_SUMMARY). */
const BATCH_SIZE = 500;
const cache = new Map();
const pending = new Set();
const inFlight = new Set();
const listeners = new Set();
let timer;
let version = 0;
function emit() {
    version++;
    listeners.forEach((listener) => listener());
}
async function flush() {
    timer = undefined;
    const ids = [...pending];
    pending.clear();
    for (let start = 0; start < ids.length; start += BATCH_SIZE) {
        const chunk = ids.slice(start, start + BATCH_SIZE);
        chunk.forEach((id) => inFlight.add(id));
        try {
            const { summary } = await SigningApi_1.SigningApi.summarizeSignatures(chunk);
            for (const id of chunk)
                cache.set(id, summary[String(id)] ?? null);
        }
        catch {
            // Bez plakietki, bez ponawiania w pętli: kolejna próba przy następnym otwarciu listy.
            for (const id of chunk)
                cache.set(id, null);
        }
        finally {
            chunk.forEach((id) => inFlight.delete(id));
        }
    }
    emit();
}
function request(letterId) {
    if (cache.has(letterId) || pending.has(letterId) || inFlight.has(letterId))
        return;
    pending.add(letterId);
    if (timer === undefined)
        timer = setTimeout(flush, BATCH_DELAY_MS);
}
/** Po nowym podpisie: zapomnij wpis, a zamontowane plakietki same zapytają ponownie. */
function invalidateLetterSignatures(letterId) {
    cache.delete(letterId);
    emit();
}
/** Tylko testy. */
function resetLetterSignaturesStore() {
    cache.clear();
    pending.clear();
    inFlight.clear();
    if (timer !== undefined)
        clearTimeout(timer);
    timer = undefined;
    emit();
}
function subscribe(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
/** Podpisy jednego pisma; `undefined` = jeszcze nie wiadomo, `null` = brak podpisów. */
function useLetterSignatures(letterId) {
    // Migawka to numer wersji, a wynik czytamy z cache — useSyncExternalStore wymaga stabilnej wartości.
    (0, react_1.useSyncExternalStore)(subscribe, () => version);
    (0, react_1.useEffect)(() => {
        if (letterId !== undefined)
            request(letterId);
    });
    if (letterId === undefined)
        return null;
    return cache.has(letterId) ? cache.get(letterId) : undefined;
}
